import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { EMPTY } from "rxjs";
import { catchError, finalize, tap } from "rxjs/operators";
import { ArpyRequirementDto } from "src/app/models/dto/arpy/arpyRequirementDto";
import { ArpyVehicleFunctionDto } from "src/app/models/dto/arpy/arpyVehicleFunctionDto";
import { ArpyVehicleSubFunctionDto } from "src/app/models/dto/arpy/arpyVehicleSubFunctionDto";
import { ArpyApiService } from "src/app/services/api/arpy-api.service";
import { ArpyVehicleFunctionsActions } from "./arpyVehicleFunctions.actions";
import { ArpyVehicleFunctionsStateModel } from "./arpyVehicleFunctions.state.model";

// Tree view for display, derived from the flat, normalized state below - every
// VF/VSF/Requirement also stays independently reachable by id (via the flat
// selectors) for linking a coverage item straight to one, regardless of where
// (or whether) it sits in the tree.
export interface ArpyVehicleFunctionTreeNode extends ArpyVehicleFunctionDto {
    requirements: ArpyRequirementDto[];
    subFunctions: (ArpyVehicleSubFunctionDto & { requirements: ArpyRequirementDto[] })[];
}

@State<ArpyVehicleFunctionsStateModel>({
    name: 'arpyVehicleFunctions',
    defaults: {
        vehicleFunctions: [],
        vehicleSubFunctions: [],
        requirements: [],
        sourceFileVersion: undefined,
        loadError: null,
        loading: false
    },
})
@Injectable()
export class ArpyVehicleFunctionsState {
    constructor(private arpyApiService: ArpyApiService) {}

    @Action(ArpyVehicleFunctionsActions.SetAllArpyVehicleFunctions)
    SetAllArpyVehicleFunctions(ctx: StateContext<ArpyVehicleFunctionsStateModel>) {
        const state = ctx.getState();
        if (state.loading || (state.vehicleFunctions && state.vehicleFunctions.length > 0))
            return EMPTY;

        ctx.patchState({ loading: true });

        return this.arpyApiService.getAllVehicleFunctions().pipe(
            tap(result => {
                ctx.patchState({
                    vehicleFunctions: [...result.vehicleFunctions].sort((a, b) => a.name?.localeCompare(b.name)),
                    vehicleSubFunctions: result.vehicleSubFunctions,
                    requirements: result.requirements,
                    sourceFileVersion: result.sourceFileVersion,
                    loadError: null
                });
            }),
            catchError(err => {
                // Handled here rather than left to propagate: an empty array alone
                // can't tell the UI "this failed" vs "still loading" vs "legitimately
                // empty" - this flag is what makes that distinction possible.
                ctx.patchState({ loadError: err?.message ?? 'Failed to load Arpy vehicle functions' });
                return EMPTY;
            }),
            finalize(() => ctx.patchState({ loading: false }))
        );
    }

    @Selector()
    static getAllVehicleFunctions(state: ArpyVehicleFunctionsStateModel) {
        return state.vehicleFunctions;
    }

    @Selector()
    static getLoadError(state: ArpyVehicleFunctionsStateModel) {
        return state.loadError;
    }

    @Selector()
    static getAllVehicleSubFunctions(state: ArpyVehicleFunctionsStateModel) {
        return state.vehicleSubFunctions;
    }

    @Selector()
    static getAllRequirements(state: ArpyVehicleFunctionsStateModel) {
        return state.requirements;
    }

    @Selector()
    static getSourceFileVersion(state: ArpyVehicleFunctionsStateModel) {
        return state.sourceFileVersion;
    }

    @Selector()
    static getVehicleFunctionTree(state: ArpyVehicleFunctionsStateModel): ArpyVehicleFunctionTreeNode[] {
        const requirementsByUid = new Map(state.requirements.map(req => [req.uid, req]));
        const subFunctionsByVfUid = new Map<string, ArpyVehicleSubFunctionDto[]>();
        for (const subFunction of state.vehicleSubFunctions) {
            const siblings = subFunctionsByVfUid.get(subFunction.vehicleFunctionUid) ?? [];
            siblings.push(subFunction);
            subFunctionsByVfUid.set(subFunction.vehicleFunctionUid, siblings);
        }

        const resolveRequirements = (uids: string[]): ArpyRequirementDto[] =>
            uids.map(uid => requirementsByUid.get(uid)).filter((req): req is ArpyRequirementDto => !!req);

        return state.vehicleFunctions.map(vf => ({
            ...vf,
            requirements: resolveRequirements(vf.requirementUids),
            subFunctions: (subFunctionsByVfUid.get(vf.uid) ?? []).map(sf => ({
                ...sf,
                requirements: resolveRequirements(sf.requirementUids)
            }))
        }));
    }
}
